import React, { useEffect, useState } from "react";
import { Modal } from "../../Components/Modal";
import { ChangeDiffViewer } from "../../Components/diff/ChangeDiffViewer";
import { auditClient } from "./auditClient";

/**
 * Diff complet d'une entrée d'audit, DEUX régimes servis par le même viewer partagé :
 * - « version » : les instantanés du DOCUMENT (prevVersionId / versionId) posés au save ;
 * - « artifact » : les archives de l'ARTEFACT DÉPLOYÉ (prevArtifactVersionId / artifactVersionId)
 *   posées au déploiement, pour voir ce que CE déploiement a changé sur la cible, exactement
 *   comme la revue de la page Publication.
 * Sans « précédent », le diff se fait contre un document vide : tout apparaît ajouté.
 */

export interface AuditDiffModalProps {
	open: boolean;
	onClose: () => void;
	contentId: string;
	versionId: number;
	prevVersionId?: number;
	/** « version » (défaut) = instantanés du document ; « artifact » = archives déployées. */
	kind?: "version" | "artifact";
	/** Cible humaine pour les libellés en régime artifact (« preview » / « production »). */
	targetLabel?: string;
	/** Titre humain de l'entrée (le message du journal), affiché en en-tête. */
	title?: string;
}

export const AuditDiffModal: React.FC<AuditDiffModalProps> = ({
	open,
	onClose,
	contentId,
	versionId,
	prevVersionId,
	kind = "version",
	targetLabel,
	title
}) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [before, setBefore] = useState<unknown>(null);
	const [after, setAfter] = useState<unknown>(null);

	useEffect(() => {
		if (!open) return;
		let cancelled = false;
		setLoading(true);
		setError(null);
		(async () => {
			try {
				const fetchOne = kind === "artifact" ? auditClient.artifact : auditClient.version;
				const current = await fetchOne(contentId, versionId);
				const previous = prevVersionId && prevVersionId !== versionId ? await fetchOne(contentId, prevVersionId) : null;
				if (cancelled) return;
				setAfter(current.data ?? {});
				// Re-deploy identique : la paire pointe le même instantané, le viewer dira « identiques ».
				setBefore(prevVersionId === versionId ? current.data ?? {} : previous?.data ?? {});
			} catch {
				if (!cancelled)
					setError(
						kind === "artifact"
							? "Impossible de charger les artefacts de ce déploiement (archives purgées ou introuvables)."
							: "Impossible de charger les versions de ce contenu (instantanés purgés ou introuvables)."
					);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [open, contentId, versionId, prevVersionId, kind]);

	const beforeLabel =
		kind === "artifact"
			? prevVersionId
				? "Artefact précédent"
				: "Premier déploiement"
			: prevVersionId
				? `Version ${prevVersionId}`
				: "Document vide";
	const afterLabel = kind === "artifact" ? `Artefact déployé${targetLabel ? ` (${targetLabel})` : ""}` : `Version ${versionId}`;
	const footer =
		kind === "artifact"
			? prevVersionId
				? `Ce que ce déploiement a changé${targetLabel ? ` en ${targetLabel}` : ""}.`
				: `Premier déploiement archivé${targetLabel ? ` en ${targetLabel}` : ""} : tout est nouveau.`
			: prevVersionId
				? `Version ${prevVersionId} comparée à la version ${versionId}`
				: `Création (version ${versionId})`;

	return (
		<Modal show={open} onHide={onClose} size="xl" centered>
			<Modal.Header closeButton>
				<Modal.Title>{title ?? "Diff complet"}</Modal.Title>
			</Modal.Header>
			<Modal.Body scrollable>
				{loading ? (
					<div className="px-5 py-16 text-center text-sm text-slate-500">
						{kind === "artifact" ? "Chargement des artefacts…" : "Chargement des versions…"}
					</div>
				) : error ? (
					<div className="px-5 py-16 text-center text-sm text-rose-600">{error}</div>
				) : (
					<ChangeDiffViewer before={before} after={after} beforeLabel={beforeLabel} afterLabel={afterLabel} />
				)}
			</Modal.Body>
			<Modal.Footer className="justify-between">
				<span className="text-xs text-slate-500">{footer}</span>
				<button
					type="button"
					onClick={onClose}
					className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
				>
					Fermer
				</button>
			</Modal.Footer>
		</Modal>
	);
};

export default AuditDiffModal;
